import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { ProgressBar, Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { LuSlidersHorizontal } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { MAP_LAYER_NAMES } from '@utils/constants';
import { VIEW3D_SIDEBAR_ID } from '@config/view3dConfig';
import { Pick } from './view3dStyles';
import { isView3dBuilding, isWaitingForGpu } from './buildStatus';
import { TIER_RANK, pickTier } from './engine/deviceProfile';
import { requestView3dBuild } from '@utils/api/view3d';

export { isView3dBuilding };
import LayerSettings, { LayerFunction } from '../../sidebars/sidebarElements/LayerSettings';
import LayerCheckbox from '../../sidebars/sidebarElements/LayerCheckbox';

const View3dSettings = lazy(() => import('./View3dSettings'));

const ChildRows = styled.ul`
	display: flex;
	flex-direction: column;
	row-gap: 10px;
	margin-top: 10px !important;
	font-weight: 400;
`;

const Row = styled.li`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

const Help = styled.small`
	display: block;
	color: ${({ theme }) => theme.colors.textAccent};
	font-weight: 400;
	word-break: normal;
	line-height: 1.4;
`;

/** What a quality is called in the app, and roughly how long it takes. */
const DETAIL_OPTIONS = [
	{ quality: 'standard', label: 'Detailed', time: 'about an hour' },
	{ quality: 'high', label: 'Most detailed', time: 'about 1.5 hours' },
];
const BUILD_ERRORS = {
	409: 'A 3D view is already being built.',
	422: 'This project is too large for a more detailed 3D view.',
	503: '3D views are switched off for now.',
};

/**
 * A sharper version of the 3D view, on request: the quick view is built automatically, the detailed ones take
 * longer on the GPU. While one builds the current model stays on screen with its progress below.
 */
const DetailControls = ({ project, manifest }) => {
	const queryClient = useQueryClient();
	const [sending, setSending] = useState(null);
	const [error, setError] = useState(null);
	const view3d = project?.view3d;
	const building = isView3dBuilding(view3d);
	const best = TIER_RANK[pickTier(manifest)] ?? -1;
	const options = DETAIL_OPTIONS.filter(o => TIER_RANK[o.quality] > best);

	if (building) {
		const parts = view3d.blocks;
		const name = DETAIL_OPTIONS.find(o => o.quality === view3d.building)?.label ?? 'New';
		return (
			<Row>
				<Spinner animation="border" size="sm" />
				<span>
					{isWaitingForGpu(view3d) ? (
						`${name} 3D view: waiting for a free GPU`
					) : (
						<>
							{name} 3D view building
							{view3d.progress != null ? ` · ${Math.round(view3d.progress)} %` : ''}
							{parts?.total > 1 ? ` · ${parts.done} of ${parts.total} parts` : ''}
						</>
					)}
				</span>
			</Row>
		);
	}
	if (!options.length) return null;

	const start = async quality => {
		setSending(quality);
		setError(null);
		try {
			await requestView3dBuild(project.uuid, quality);
			await queryClient.invalidateQueries({ queryKey: ['project', project.uuid] });
		} catch (err) {
			setError(
				BUILD_ERRORS[err?.response?.status] ??
					'The 3D view could not be started. Please try again.'
			);
		} finally {
			setSending(null);
		}
	};
	const failed = view3d?.status === 'failed' && TIER_RANK[view3d?.building] > best;

	return (
		<>
			<Row>
				<span>Sharper:</span>
				<Pick aria-label="Build a more detailed 3D view">
					{options.map(o => (
						<button
							key={o.quality}
							type="button"
							disabled={!!sending}
							title={`Builds a sharper 3D view on a GPU; takes ${o.time}. The current one stays until it is ready.`}
							onClick={() => start(o.quality)}
						>
							{sending === o.quality ? 'Starting…' : o.label}
						</button>
					))}
				</Pick>
			</Row>
			{(error || failed) && (
				<Help>
					{error ??
						(view3d.reason_code === 'no_gpu_capacity'
							? 'No GPU was free for the detailed 3D view. Please try again later.'
							: 'The detailed 3D view could not be built.')}
				</Help>
			)}
		</>
	);
};

/**
 * A project whose first 3D view could not be built (usually no GPU was free): says so and offers to try again.
 */
const RetryFirstView = ({ project }) => {
	const queryClient = useQueryClient();
	const [sending, setSending] = useState(false);
	const [error, setError] = useState(null);
	const quality = project.view3d.building ?? 'preview';

	const retry = async () => {
		setSending(true);
		setError(null);
		try {
			await requestView3dBuild(project.uuid, quality);
			await queryClient.invalidateQueries({ queryKey: ['project', project.uuid] });
		} catch (err) {
			setError(
				BUILD_ERRORS[err?.response?.status] ??
					'The 3D view could not be started. Please try again.'
			);
		} finally {
			setSending(false);
		}
	};

	return (
		<li>
			<div className="d-flex align-items-center gap-2">
				<span style={{ fontWeight: 400 }}>3D view</span>
				<Pick aria-label="Build the 3D view again">
					<button type="button" disabled={sending} onClick={retry}>
						{sending ? 'Starting…' : 'Try again'}
					</button>
				</Pick>
			</div>
			<Help>
				{error ??
					(project.view3d.reason_code === 'no_gpu_capacity'
						? 'No GPU was free to build the 3D view.'
						: 'The 3D view could not be built.')}
			</Help>
		</li>
	);
};

/**
 * The "3D view" entry of the IMAGERY layer group: opens the 3D view and toggles what is shown in it.
 * Renders nothing for projects without a 3D view.
 */
const View3dLayerEntry = () => {
	const { project, view3d, dispatch, setActiveSidebar, removeFromActiveSidebars, detections } =
		useProject();
	const { manifest, manifestStatus, active, layers, pointCloud } = view3d;
	const building = isView3dBuilding(project?.view3d) && manifestStatus !== 'ready';

	// Close the settings panel when the entry unmounts (the function is recreated every render)
	const removeSidebarRef = useRef(removeFromActiveSidebars);
	removeSidebarRef.current = removeFromActiveSidebars;
	useEffect(() => () => removeSidebarRef.current(VIEW3D_SIDEBAR_ID), []);

	if (building) {
		const progress =
			project.view3d.progress ??
			project.view3d.quality?.progress ??
			project.view3d.preview?.progress;
		const parts = project.view3d.blocks;
		if (isWaitingForGpu(project.view3d)) {
			return (
				<li>
					<div className="d-flex align-items-center gap-2">
						<Spinner animation="border" size="sm" />
						<span style={{ fontWeight: 400 }}>3D view: waiting for a free GPU</span>
					</div>
					<Help>
						All GPUs are busy right now. The build starts by itself as soon as one is
						free.
					</Help>
				</li>
			);
		}
		return (
			<li>
				<div className="d-flex align-items-center gap-2">
					<Spinner animation="border" size="sm" />
					<span style={{ fontWeight: 400 }}>
						Building 3D view{progress != null ? ` · ${Math.round(progress)} %` : ''}
						{parts?.total > 1 ? ` · ${parts.done} of ${parts.total} parts` : ''}
					</span>
				</div>
				{progress != null && (
					<ProgressBar now={progress} className="mt-2" style={{ height: 6 }} />
				)}
				<Help>The 3D view shows up here when it is ready.</Help>
			</li>
		);
	}

	if (manifestStatus === 'none' && project?.view3d?.status === 'failed')
		return <RetryFirstView project={project} />;
	if (manifestStatus !== 'ready' || !manifest) return null;

	const hasPointCloud = manifest.point_cloud?.status === 'ready';
	const setActive = value => dispatch({ type: 'setView3d', payload: { active: value } });
	const setLayer = (key, value) =>
		dispatch({ type: 'setView3dLayers', payload: { [key]: value } });

	return (
		<li>
			<LayerSettings
				id="view3d-layer"
				label={MAP_LAYER_NAMES.gaussian_splat}
				checked={active}
				defaultState={active}
				onCheck={() => setActive(true)}
				onUncheck={() => setActive(false)}
				layerFunctions={[
					{
						icon: <LuSlidersHorizontal />,
						title: '3D view settings',
						onClick: () =>
							setActiveSidebar({
								sidebarId: VIEW3D_SIDEBAR_ID,
								parentId: 'layerView',
							}),
					},
				]}
			/>

			<ChildRows>
				{active && (
					<>
						<Row>
							<LayerCheckbox
								id="view3d-model"
								label="Photo-realistic model"
								checked={layers.model}
								defaultChecked={layers.model}
								onCheck={() => setLayer('model', true)}
								onUncheck={() => setLayer('model', false)}
							/>
						</Row>
						{hasPointCloud && (
							<Row>
								<LayerCheckbox
									id="view3d-point-cloud"
									label="Point cloud"
									checked={layers.pointCloud}
									defaultChecked={layers.pointCloud}
									onCheck={() => setLayer('pointCloud', true)}
									onUncheck={() => setLayer('pointCloud', false)}
								/>
								{layers.pointCloud && (
									<Pick aria-label="Point cloud colours">
										<button
											type="button"
											className={pointCloud.colorMode === 'photo' ? 'on' : ''}
											onClick={() =>
												dispatch({
													type: 'setView3dPointCloud',
													payload: { colorMode: 'photo' },
												})
											}
										>
											Photo
										</button>
										<button
											type="button"
											className={
												pointCloud.colorMode === 'height' ? 'on' : ''
											}
											onClick={() =>
												dispatch({
													type: 'setView3dPointCloud',
													payload: { colorMode: 'height' },
												})
											}
										>
											Height
										</button>
									</Pick>
								)}
							</Row>
						)}
						{detections?.length > 0 && (
							<Row>
								<LayerCheckbox
									id="view3d-detections"
									label="Detections"
									checked={layers.detections !== false}
									defaultChecked={layers.detections !== false}
									onCheck={() => setLayer('detections', true)}
									onUncheck={() => setLayer('detections', false)}
								/>
							</Row>
						)}
						<Row>
							<LayerCheckbox
								id="view3d-globe"
								label="Map under the model"
								checked={layers.globe}
								defaultChecked={layers.globe}
								onCheck={() => setLayer('globe', true)}
								onUncheck={() => setLayer('globe', false)}
							/>
						</Row>
					</>
				)}
			</ChildRows>

			{project?.view3d?.reason_code === 'partial' && (
				<Help>
					Part of the area could not be built; the rest of the 3D view is complete.
				</Help>
			)}

			{active && (
				<ChildRows>
					<DetailControls project={project} manifest={manifest} />
				</ChildRows>
			)}

			<Suspense fallback={null}>
				<View3dSettings />
			</Suspense>
		</li>
	);
};

export default View3dLayerEntry;
